import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { Checkbox, Flex, IconButton, Input } from '@chakra-ui/react'
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PathsE } from "shared/routes";
import { DialogMessageDTO } from "shared/api/types/dialog_messages";
import { formatDateToHuman } from "shared/utils/formattings";
import MuiIcon from "components/MuiIcon";
import useGetColors from "shared/hooks/useGetColors";
import RowDragHandleCell from "shared/components/table/RowDragHandleCell";

export const useGetGetBreadcrumbs = () => {
  const { t } = useTranslation();
  return [
    {
      path: PathsE.HOME,
      text: t('general.home'),
    },
  ];
}

interface Params {
  handleDelete: (messageId: DialogMessageDTO['id']) => Promise<FixMeLater>;
  handleUpdate: (messageId: DialogMessageDTO['id'], newText: string) => void;
  isDeletePending: boolean,
}

export const useGetColumns = ({ handleDelete, handleUpdate, isDeletePending }: Params) => {
  const { t } = useTranslation();
  const { red500, blue500 } = useGetColors();
  const columnHelper = createColumnHelper<DialogMessageDTO>();
  const [editingMessageId, setEditingMessageId] = useState<DialogMessageDTO['id'] | null>(null);
  const [deletingMessageId, setDeletingMessageId] = useState<DialogMessageDTO['id'] | null>(null);

  const handleEditRow = (messageId: DialogMessageDTO['id']) => {
    setEditingMessageId(messageId)
  }

  const handleSaveRow = (messageId: DialogMessageDTO['id'], text: string) => {
    handleUpdate(messageId, text);
    setEditingMessageId(null);
  }

  const handleDeleteRow = async (messageId: DialogMessageDTO['id']) => {
    setDeletingMessageId(messageId);
    await handleDelete(messageId);
    setDeletingMessageId(null);
  }

  return useMemo(
    () => [
      columnHelper.accessor('id', {
        cell: ({ row }) => {
          const sendTime = row.original.sendTime;
          if (sendTime && new Date(sendTime) <= new Date()) {
            return null;
          }
          return <RowDragHandleCell  />;
        },
        header: '',
        id: 'id1',
      }),
      {
        id: 'select',
        cell: ({ row }) => (
          <div className="px-1">
            <Checkbox
              {...{
                checked: row.getIsSelected(),
                disabled: !row.getCanSelect(),
                indeterminate: String(row.getIsSomeSelected()),
                onChange: row.getToggleSelectedHandler(),
              }}
            />
          </div>
        ),
      },
      columnHelper.accessor('sendTime', {
        cell: ({ getValue }) => (
          <span>{getValue() ? '🟢' : ''}</span>
        ),
        header: '',
        id: 'sendStatus1',
      }),
      columnHelper.accessor('clientName', {
        header: t('Client name'),
        id: 'clientName',
      }),
      columnHelper.accessor('text', {
        header: t('Message'),
        id: 'text',
        cell: ({ getValue, row }) => {
          const value = getValue();
          const messageId = row.original.id;
          const [inputValue, setInputValue] = useState(value);

          const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            setInputValue(event.target.value);
          }

          if (!value) return null;
          if (messageId === editingMessageId) {
            return (
              <Input
                value={inputValue}
                onChange={onChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSaveRow(messageId, inputValue);
                  }
                }}
                type="text"
                fontSize="16px"
              />
            )
          }
          return <span onClick={() => handleEditRow(messageId)}>{value}</span>
        },
      }),
      columnHelper.accessor('sendTime', {
        header: t('Send time'),
        id: 'sendTime',
        cell: ({ getValue }) => {
          const value = getValue();
          if (!value) return null;
          return (
            <span>{formatDateToHuman(getValue())}</span>
          )
        },
        meta: {
          isNumeric: true,
          styles: {
            sizes: {
              width: '140px'
            }
          }
        }
      }),
      columnHelper.accessor('editTime', {
        header: t('Edit time'),
        id: 'editTime',
        cell: ({ getValue }) => {
          const value = getValue();
          if (!value) return null;
          return (
            <span>{formatDateToHuman(getValue())}</span>
          )
        },
        meta: {
          isNumeric: true,
          styles: {
            sizes: {
              width: '130px'
            }
          }
        }
      }),
      // columnHelper.accessor('datetimeOfCreate', {
      //   header: t('services.table.createdAt'),
      //   id: 'createdAt',
      //   cell: ({ getValue }) => {
      //     const value = getValue();
      //     if (!value) return null;
      //     return (
      //       <span>{formatDateToHuman(getValue())}</span>
      //     )
      //   },
      //   meta: {
      //     isNumeric: true,
      //     styles: {
      //       sizes: {
      //         width: '130px'
      //       }
      //     }
      //   }
      // }),
      columnHelper.accessor('id', {
        header: '',
        id: 'actions',
        cell: ({ getValue }) => {
          const messageId = getValue();
          return (
            <Flex>
              {messageId === editingMessageId ? (
                <IconButton
                  mr="2"
                  aria-label="Close"
                  onClick={() => setEditingMessageId(null)}
                  borderRadius="10px"
                  backgroundColor="transparent"
                  icon={<MuiIcon name="close" color={blue500} />}
                />
              ) : (
                <IconButton
                  mr="2"
                  aria-label="Edit"
                  onClick={() => handleEditRow(messageId)}
                  borderRadius="10px"
                  backgroundColor="transparent"
                  icon={<MuiIcon name="edit" color={blue500} />}
                />
              )}
              <IconButton
                aria-label="Delete"
                onClick={() => handleDeleteRow(messageId)}
                isLoading={isDeletePending && messageId === deletingMessageId}
                borderRadius="10px"
                backgroundColor="transparent"
                icon={<MuiIcon name="delete" color={red500} />}
              />
            </Flex>
          )
        },
      }),
    ] as ColumnDef<DialogMessageDTO>[],
    [t, editingMessageId, isDeletePending]
  );
};
